/*
 * MappingPersons.tsx
 * Author: fwunderlich
 * Date: 15.04.2024
 *
 * Copyright: DMT GmbH & Co. KG
 */

import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {AutoComplete} from 'primereact/autocomplete';
import {MappingInputs} from './MappingInputs';
import {Button} from 'primereact/button';
import {useGetParcelQuery} from '../../redux/rtk/injectedParcelApi';
import {useGetPersonQuery} from '../../redux/rtk/injectedPersonApi';
import {getPersonLabel, LandRegister, Parcel, Person} from '../functions/GlobalData';
import {MappingType} from '../../redux/slices/MappingSlice';
import {useGetLandRegisterQuery} from '../../redux/rtk/injectedLandregisterApi';
import Input from '../global/Input';
import {CustomConfirmDialog} from '../global/CustomConfirmDialog';

type Props = {
    projectId: string,
    activeMappings: string[],
    persons: any[],
    onChange: (persons: any[]) => void,
}

/**
 * Form for editing and creating of users
 * @param props
 * @constructor
 */
export const MappingPersons = (props: Props): JSX.Element => {

    const {t} = useTranslation(['common', 'globalData', 'toasts']);

    const [autoCompleteItems, setAutoCompleteItems] = useState<any>([]);

    const placeholder = t('globalData:global.mapping.searchMappingPerson');

    const [fields, setFields] = useState<any[]>(props.persons || []);

    useEffect(() => {
        props.onChange(fields);
    }, [fields]);

    const persons = useGetPersonQuery({
        // projectId: props.projectId,
        projectId: '',
        personId: '',
        commonData: true
    });

    const onAutoComplete = (e: any, index: number) => {
        setFields((prevState: any) => {
            const tempFields = JSON.parse(JSON.stringify(prevState));

            for (let i = 0; i < tempFields.length; i++) {
                const item = tempFields[i];

                if (item.index === index) {
                    item.person = e.value;
                    break;
                }
            }

            return tempFields;
        });

        // setSelectedAutoCompleteItem(e.value);
    };

    const searchItems = (event: any) => {
        let temp: any[] = [];
        const key = event.query.toLowerCase();

        if (persons.data) {
            temp = persons.data.filter((item: Person) => {
                return (personSearch(key, item) && props.activeMappings.indexOf(item.person_id) < 0);
            }).map((item: Person) => {
                return {label: getPersonLabel(item), value: item.person_id};
            });
        }

        setAutoCompleteItems(temp);
    };

    const personSearch = (key: string, person: Person): boolean => {
        const results = [];


        const keys = key.split(' ');

        for (let i = 0; i < keys.length; i++) {
            const k = formatValue(keys[i]);

            let tempResult = false;

            if (person.name) {
                tempResult = tempResult || formatValue(person.name).indexOf(k) >= 0;
            }

            if (person.vorname) {
                tempResult = tempResult || formatValue(person.vorname).indexOf(k) >= 0;
            }

            if (person.adresse) {
                for (let i = 0; i < person.adresse.length; i++) {
                    const adresse = person.adresse[i];

                    if (((person.ist_juristisch && adresse.adresse_type_id === 6) || (!person.ist_juristisch && adresse.adresse_type_id === 1)) && adresse.ladungsadresse) {

                        if (adresse.ort) {
                            tempResult = tempResult || formatValue(adresse.ort).indexOf(k) >= 0;
                        }

                        if (adresse.strassehausnummer) {
                            tempResult = tempResult || formatValue(adresse.strassehausnummer).indexOf(k) >= 0;
                        }

                        break;
                    }
                }
            }

            results.push(tempResult);
        }

        return results.indexOf(false) < 0;
    };

    const formatValue = (value: string) => {
        let result = value;

        result = result.replace(',', '');
        result = result.replace('.', '');
        result = result.replace('/', '');
        result = result.toLowerCase();

        return result;
    };

    return (<div>
        <div className={'flex justify-content-end'}>
            <div className={''}>
                <Button
                    className={'p-button-success p-button-outlined p-button-icon-only'}
                    style={{left: '0', bottom: '0'}}
                    type={'button'} icon={'pi pi-plus'} onClick={() => {

                    setFields((prevState: any) => {
                        const temp = JSON.parse(JSON.stringify(prevState));

                        temp.push({person: {}, anteil: '', index: temp.length});

                        return temp;
                    });
                }}/>
            </div>
        </div>
        {fields && fields.map((item: any, index: number) => {
            return (<div className={'grid'}>
                <div className={'col-8'}>
                    <label>{(item.index + 1) + '. ' + t('globalData:persons.person')}</label>
                    <AutoComplete inputClassName={'w-full'} className={'w-full mb-2'}
                                  value={item.person}
                                  suggestions={autoCompleteItems}
                                  field={'label'}
                                  completeMethod={searchItems} onChange={(e: any) => {
                        onAutoComplete(e, index);
                    }} placeholder={placeholder}
                    ></AutoComplete>
                </div>
                <div className={'col'}>
                    <label>{t('globalData:landRegister.anteil')}</label>
                    <Input key={item.id + '_' + index} edit={true}
                           value={item.anteil} name={'anteil'} onChange={(e: any) => {
                        setFields((prevState: any) => {
                            const tempFields = JSON.parse(JSON.stringify(prevState));

                            for (let i = 0; i < tempFields.length; i++) {
                                const item = tempFields[i];

                                if (item.index === index) {
                                    item.anteil = e.target.value;
                                    break;
                                }
                            }

                            return tempFields;
                        });
                    }}/>
                </div>
                <div className={'col-fixed m-auto'}>
                    <Button className={'p-button-rounded p-button-danger p-button-text'}
                            type={'button'}
                            icon={'pi pi-trash'}
                            onClick={() => {
                                setFields((prevState: any) => {
                                    const tempFields = JSON.parse(JSON.stringify(prevState));

                                    for (let i = 0; i < tempFields.length; i++) {
                                        const item = tempFields[i];

                                        if (item.index === index) {
                                            tempFields.splice(i, 1);
                                            break;
                                        }
                                    }

                                    for (let i = 0; i < tempFields.length; i++) {
                                        const item = tempFields[i];
                                        item.index = i;
                                    }

                                    return tempFields;
                                });
                            }}/>
                </div>
            </div>);
        })}
    </div>);
};
